import { useState } from 'react';
import { Search, Bell, Plus, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '@/context/AppContext';

const PAGE_TITLES = {
  '/dashboard': 'Dashboard',
  '/creators': 'Creators',
  '/brands': 'Brands',
  '/drive': 'Reelia Drive',
  '/create': 'Create',
  '/publish': 'Publish',
  '/risk': 'Risk Center',
  '/viral': 'Viral Engine',
  '/billing': 'Billing',
  '/settings': 'Settings',
};

const MOCK_NOTIFICATIONS = [
  { id: 1, text: 'AlexFit Reel exported successfully', time: '2m ago', type: 'success' },
  { id: 2, text: 'Risk scan flagged 1 item in Mindset Daily', time: '18m ago', type: 'warning' },
  { id: 3, text: 'Trial expires in 5 days', time: '1h ago', type: 'info' },
  { id: 4, text: 'MiaVlog post scheduled for tomorrow', time: '3h ago', type: 'success' },
];

export const TopBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeBrand, trialActive, trialDaysLeft } = useApp();
  const [searchVal, setSearchVal] = useState('');
  const [notifOpen, setNotifOpen] = useState(false);

  const pageTitle = PAGE_TITLES[location.pathname] || 'Reelia';

  return (
    <header className="topbar">
      <div className="flex items-center gap-3 flex-1">
        {/* Page title */}
        <h1 className="text-sm font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
          {pageTitle}
        </h1>
        {activeBrand && (
          <>
            <span style={{ color: 'hsl(var(--foreground-subtle))' }}>/</span>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ background: activeBrand.color }} />
              <span className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>{activeBrand.name}</span>
            </div>
          </>
        )}
      </div>

      {/* Search */}
      <div className="relative hidden md:block">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'hsl(var(--foreground-subtle))' }} />
        <input
          className="reelia-input pl-8 w-56 h-8 text-xs"
          placeholder="Search assets, posts..."
          value={searchVal}
          onChange={e => setSearchVal(e.target.value)}
        />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {/* Quick create */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" className="h-8 gap-1.5 text-xs" style={{ background: 'hsl(var(--primary))', color: '#fff' }}>
              <Plus size={14} />
              Create
              <ChevronDown size={12} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => navigate('/create?tab=clips')}>YouTube Clips</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/create?tab=talking')}>Talking Camera</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/create?tab=reels')}>Reel Project</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/publish')}>Schedule Post</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Notifications */}
        <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
          <DropdownMenuTrigger asChild>
            <button className="relative p-1.5 rounded-md hover:bg-surface-hover"
              style={{ color: 'hsl(var(--foreground-muted))' }}
            >
              <Bell size={16} />
              <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full"
                style={{ background: 'hsl(var(--primary))' }}
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <div className="px-3 py-2 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
              <p className="text-xs font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Notifications</p>
            </div>
            {MOCK_NOTIFICATIONS.map(n => (
              <div key={n.id} className="flex items-start gap-3 px-3 py-2.5 hover:bg-surface-hover cursor-pointer">
                <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                  style={{
                    background: n.type === 'success' ? 'hsl(var(--success))'
                      : n.type === 'warning' ? 'hsl(var(--warning))'
                      : 'hsl(var(--primary))'
                  }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs" style={{ color: 'hsl(var(--foreground))' }}>{n.text}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--foreground-subtle))' }}>{n.time}</p>
                </div>
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
